/**
 * Reusable signature pad — pure HTML5 canvas, no library deps.
 * Touch + mouse + stylus. Returns PNG data URL via getDataURL().
 *
 * Mobile-optimized: prevents page scroll while drawing.
 */
import React, { useEffect, useImperativeHandle, useRef, useState } from "react";

const SignaturePad = React.forwardRef(({ height = 220, className = "" }, ref) => {
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef = useRef({ x: 0, y: 0 });
  const [hasInk, setHasInk] = useState(false);

  // Re-size canvas to its CSS pixel dimensions × DPR for crisp ink.
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    const resize = () => {
      const rect = c.getBoundingClientRect();
      c.width  = rect.width  * dpr;
      c.height = rect.height * dpr;
      const ctx = c.getContext("2d");
      ctx.scale(dpr, dpr);
      ctx.lineWidth = 2.2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = "#0f172a";
      ctxRef.current = ctx;
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  const pos = (evt) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = evt.touches ? evt.touches[0] : evt;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };

  const start = (evt) => {
    evt.preventDefault();
    drawingRef.current = true;
    lastRef.current = pos(evt);
  };
  const move = (evt) => {
    if (!drawingRef.current) return;
    evt.preventDefault();
    const p = pos(evt);
    const ctx = ctxRef.current;
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastRef.current = p;
    if (!hasInk) setHasInk(true);
  };
  const end = (evt) => {
    if (evt) evt.preventDefault();
    drawingRef.current = false;
  };

  useImperativeHandle(ref, () => ({
    clear: () => {
      const c = canvasRef.current;
      ctxRef.current.clearRect(0, 0, c.width, c.height);
      setHasInk(false);
    },
    getDataURL: () => canvasRef.current?.toDataURL("image/png"),
    isEmpty: () => !hasInk,
  }));

  return (
    <canvas
      ref={canvasRef}
      data-testid="signature-pad-canvas"
      style={{ width: "100%", height, touchAction: "none", cursor: "crosshair" }}
      className={`rounded-2xl border-2 border-dashed border-slate-300 dark:border-white/15 bg-white ${className}`}
      onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
      onTouchStart={start} onTouchMove={move} onTouchEnd={end} onTouchCancel={end}
    />
  );
});
SignaturePad.displayName = "SignaturePad";
export default SignaturePad;
